import { Button, Divider, Flex, Heading, Link, ProgressCircle, Text, View } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import { error } from '@react/react-spectrum/Toast';
import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { excApi, handleApiCall } from '@exchange-frontends/api';
import { useIsMounted } from '@exchange-frontends/custom-hooks';

import { UserDataContext } from '../App';

const StepCircle = styled.div`
  min-width: ${({ $isMobileSized }) => ($isMobileSized ? '30px' : '40px')};
  min-height: ${({ $isMobileSized }) => ($isMobileSized ? '30px' : '40px')};
  background: gray;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $isMobileSized }) => ($isMobileSized ? '15px' : '20px')};
`;

const StyledList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-position: inside;
`;

/*Heading component in v3 doesnot have stying properties for color,
 It will inherit from parent. So this custom component is required
*/
const CustomContainer = styled.div`
  color: black;
`;

const BoldParagraph = styled.p`
  font-weight: bold;
  margin: 16px 0;
`;

const CCInstallationInstructions = () => {
  const { matchedBreakpoints } = useBreakpoint();
  const isMobileSized = !matchedBreakpoints.includes('L');
  const { name: encodedName, versionId } = useParams();
  const name = decodeURIComponent(encodedName);
  const { accessToken } = useContext(UserDataContext) ?? {};
  const [urlLoading, setUrlLoading] = useState(false);
  const isMounted = useIsMounted();

  const downloadPlugin = () => {
    setUrlLoading(true);
    const handler = handleApiCall(excApi.downloadPlugin, { accessToken, versionId });
    (async () => {
      try {
        const { url } = (await handler.run()) ?? {};
        if (isMounted.current) {
          window.open(url, '_self');
        }
      } catch (err) {
        console.error(err);
        if (isMounted.current) {
          error('Failed to fetch download link.', { timeout: 0 });
        }
      } finally {
        if (isMounted.current) {
          setUrlLoading(false);
        }
      }
    })();
  };

  return (
    <CustomContainer>
      <View marginY="size-500" maxWidth="1920px" width={isMobileSized ? '90vw' : '80vw'} marginX="auto">
        <Helmet>
          <title>{`Installation instructions for CC plugins | Adobe Exchange`}</title>
          <meta name="robots" content="noodp, FOLLOW, INDEX" />
          <meta name="title" content={`Installation instructions for CC plugins | Adobe Exchange`} />
          <meta property="og:title" content={`Installation instructions for CC plugins | Adobe Exchange`} />
          <meta name="description" content={`Installation instructions for CC plugins`} />
          <meta property="og:description" content={`Installation instructions for CC plugins`} />
          <meta property="og:type" content="article" />
        </Helmet>
        <View
          backgroundColor="static-white"
          borderRadius="regular"
          borderColor="gray-200"
          borderWidth="thin"
          padding={isMobileSized ? 'size-200' : 'size-400'}
          fontSize="18px"
        >
          <Heading level={3}>Install this extension using CCD / Download using an Extension Install Utility</Heading>
          <Divider width="100%" size="S" />
          <BoldParagraph>
            Starting in November 2020, Adobe Exchange has removed the “auto-install” feature. After acquisition, plugins
            need to be manually installed from Creative Cloud desktop App. If you don&apos;t believe your plugin was
            installed, try these three steps:
          </BoldParagraph>
          <Flex direction="column" gap="size-400">
            <Flex alignItems="start" gap={isMobileSized ? '16px' : '24px'}>
              <StepCircle $isMobileSized={isMobileSized}>
                <strong>1</strong>
              </StepCircle>

              <StyledList>
                <li>
                  Open Creative Cloud Desktop App &amp; then go to “<b>Marketplace</b>” tab on top left.
                </li>
                <li>
                  Click on “<b>Manage Plugins</b>” it will show all the Add-ons you have acquired so far.
                </li>
                <li>
                  Click on “<b>Install</b>” button on the Add-on card you intend to download &amp; install.
                </li>
                <li>
                  You will see a spinning wheel in the card for that Add-on. Please wait for the Add-on to be installed.
                </li>
                <li>
                  If it failed, please provide asupport@adobe.com with the exact error message and its screenshot.&nbsp;
                </li>
                <li>
                  If it says successful, please read the&nbsp;&apos;<b>Where to Find it</b>&apos;&nbsp;section on the
                  add-on homepage (click on Add-on name) which will tell you where you can find the product after it has
                  been installed.&nbsp;
                </li>
              </StyledList>
            </Flex>

            <Flex alignItems="center" gap={isMobileSized ? '16px' : '24px'}>
              <StepCircle $isMobileSized={isMobileSized}>
                <strong>2</strong>
              </StepCircle>

              <Heading level={4}>
                Try this{' '}
                <Link isQuiet>
                  <a
                    href="https://helpx.adobe.com/in/creative-cloud/kb/troubleshoot-common-addon-installation-issues.html"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Helpx
                  </a>
                </Link>{' '}
                document.
              </Heading>
            </Flex>
            <Flex alignItems="start" gap={isMobileSized ? '16px' : '24px'}>
              <StepCircle $isMobileSized={isMobileSized}>
                <strong>3</strong>
              </StepCircle>

              <div>
                <Text>
                  If you are an enterprise customer of Creative Cloud Apps and your administrator will not let you load
                  Creative Cloud Desktop on your PC, follow these instructions to download the extension and install it
                  with an Extension Install Utility.
                </Text>
                <h4>Download your extension (.zxp file)</h4>
                <p>
                  Download your extension using the button below, to download the zxp file to your computer&apos;s
                  Downloads folder.
                </p>

                <Button
                  variant="cta"
                  marginBottom="size-100"
                  onClick={downloadPlugin}
                  isDisabled={urlLoading || !accessToken || !versionId}
                >
                  {urlLoading && (
                    <ProgressCircle size="S" isIndeterminate marginEnd="size-100" aria-label="Fetching download link" />
                  )}
                  Download {name}
                </Button>

                <p>Enter any passwords, or accept any notices your computer&apos;s operating system presents.</p>
                <h4>Install your extension with an Adobe compatible Extension Install Utility</h4>
                <p>
                  If you were unable to install your extension using the Creative Cloud desktop app, please follow the
                  instructions below to use an extension install utility such as Anastasiy&apos;s Extension Manager. If
                  you are looking to install&nbsp;a decrypted extension (as we don&apos;t support encrypted extensions
                  for enterprise use cases)&nbsp;for multiple Creative Cloud for teams or Creative Cloud for enterprise
                  users you can deploy using the CC Packager.
                </p>
                <p>
                  <Link isQuiet>
                    <a
                      href="https://helpx.adobe.com/enterprise/using/manage-extensions.html"
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Learn more about the installation of an extension"
                    >
                      <b>Learn more</b>
                    </a>
                  </Link>
                </p>
                <p>
                  Open&nbsp;Anastasiy&apos;s Extension Manager or your chosen Extension Install Utility. If you
                  don&apos;t have it on your computer, below are download links for alternative utilities. Once
                  downloaded and installed, use an extension install utility to install your .zxp files.
                </p>
                <p>
                  <Link isQuiet>
                    <a href="https://install.anastasiy.com/" target="_blank" rel="noreferrer">
                      <b>Download Anastasiy&apos;s Extension Manager</b>
                    </a>
                  </Link>
                </p>
                <p>
                  After selecting either the Mac or Windows version of your chosen Extension Install Utility, download
                  it to your computer and follow the installation instructions from the installer, which should be in
                  your Downloads folder.
                </p>
                <p>
                  Install your extension from Adobe Exchange by selecting File &gt; Install extension or following the
                  instructions within the install utility and selecting the extension from within your computer&apos;s
                  Downloads folder.
                </p>
                <h4>Check &apos;Where to Find it&apos;</h4>
                <p>
                  Follow the instructions in the &apos;Where to find it&apos; section on the product details page. Some
                  products may require you to carry out further steps to install.
                </p>
              </div>
            </Flex>
          </Flex>
        </View>
      </View>
    </CustomContainer>
  );
};

export default CCInstallationInstructions;
